import { Injectable } from '@angular/core';
import { HttpService } from '@core/services/http/http-api';
import {
  ORGANIZATION,
  USER,
  USER_MAINTENANCE,
  SUPPORT_TICKET,
  LOCATION_MAINTENANCE,
  ADMIN_CONSOLE
} from '@core/constants/api.constants';

/**
 * Service for the Support Ticket feature.
 *
 * Endpoints:
 *  - Org list:        USER.GET_ORGS
 *  - Stockroom list:  ORGANIZATION.GET_STOCKROOM_LIST(orgId)
 *  - User list:       USER_MAINTENANCE.GET_USERS(type)
 *  - Locations:       LOCATION_MAINTENANCE.GET_ALL_LOCATION
 *  - Submit defect:   SUPPORT_TICKET.POST_SUBMIT_DEFECT  (JSON body)
 *  - Screenshots:     SUPPORT_TICKET.POST_DEFECT_SCREENSHOTS(supportTicketId)
 *                     (multipart/form-data — files attached under "files")
 */
@Injectable({
  providedIn: 'root',
})
export class SupportTicketService {

  constructor(private httpService: HttpService) { }

  /** Fetches the list of organizations available to the current user. */
  getOrganizations(): Promise<any> {
    return this.httpService.get(USER.GET_ORGS);
  }

  /** Fetches the stockrooms for the given org id. */
  getStockrooms(orgId: string): Promise<any> {
    return this.httpService.get(ORGANIZATION.GET_STOCKROOM_LIST(orgId));
  }

  /** Fetches the list of users for the given user type (kept for legacy callers). */
  getUsers(type: string = 'internal'): Promise<any> {
    return this.httpService.get(USER_MAINTENANCE.GET_USERS(type));
  }

  /** Fetches users assigned to the selected organization for the "Others" flow. */
  async getOrganizationUsers(orgId: string): Promise<any> {
    try {
      return await this.httpService.get(ADMIN_CONSOLE.GET_LOAD_ORGANIZATION_USER(orgId));
    } catch (orgUserError) {
      // Some environments do not expose loadorguser for the current route/user.
      // Fall back to the existing active-users endpoint so the dropdown can still populate.
      return this.getUsers('INTERNALACTIVE');
    }
  }

  /**
   * Fetches all locations for the Location dropdown.
   * Response shape: { data: [{ id, locationName, ... }, ...], totalCount, count }
   */
  getLocations(): Promise<any> {
    return this.httpService.get(LOCATION_MAINTENANCE.GET_ALL_LOCATION);
  }

  /**
   * Step 1 of submit: save the ticket / defect details (no files).
   * Backend is expected to return the new ticket id in the response — we look for
   * any of the common keys (`supportTicketId`, `id`, `data.supportTicketId`, etc.)
   * in the caller.
   */
  submitDefect(payload: any): Promise<any> {
    return this.httpService.post(SUPPORT_TICKET.POST_SUBMIT_DEFECT, payload);
  }

  /**
   * Step 2 of submit: upload screenshots / attachments for the just-created ticket.
   * `supportTicketId` comes from the response of submitDefect().
   * `files` is the raw File[] gathered from the form.
   */
  uploadDefectScreenshots(supportTicketId: string | number, files: File[]): Promise<any> {
    const formData = new FormData();
    for (const file of files) {
      // Most Spring/Java endpoints accept the multipart field name "files"
      // (multi-value). If the backend expects a different name (e.g. "screenshots")
      // change the key below.
      formData.append('screenshots', file, file.name);
    }
    return this.httpService.post(SUPPORT_TICKET.POST_DEFECT_SCREENSHOTS(supportTicketId), formData);
  }

  /**
   * @deprecated kept for the spec file — the form now calls submitDefect() and
   * uploadDefectScreenshots() in sequence.
   */
  submitTicket(payload: any): Promise<any> {
    // eslint-disable-next-line no-console
    console.log('[SupportTicketService] (legacy) submitTicket payload:', payload);
    return Promise.resolve({ status: 'success', message: 'Support ticket submitted successfully.' });
  }
}
